import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type GeneralPurchaseReportRow,
  type ProductPurchaseReportRow,
  type PurchaseReportFilterOptions,
  type PurchaseReportFilters,
  type PurchaseReportResult,
  type PurchaseReportType,
  type SupplierPurchaseReportRow,
} from '../../../services/reports.service'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

import { AccountsPayableReport, type AccountsPayableReportContext } from './AccountsPayableReport'

const DEFAULT_FILTERS: Record<PurchaseReportType, PurchaseReportFilters> = {
  general: {},
  products: {},
  suppliers: {},
}
const HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const CELL_CLASS = 'px-6 py-4 text-table-foreground'

type PurchaseRow =
  | GeneralPurchaseReportRow
  | ProductPurchaseReportRow
  | SupplierPurchaseReportRow

export interface AppliedPurchaseReportContext {
  type: PurchaseReportType
  filters: PurchaseReportFilters
}

export type PurchaseReportTab = PurchaseReportType | 'accounts-payable-report'

interface ComprasTabProps {
  onAccountsPayableContextChange?: (context: AccountsPayableReportContext | null) => void
  activeReport: PurchaseReportTab
  onActiveReportChange: (report: PurchaseReportTab) => void
  onAppliedContextChange: (context: AppliedPurchaseReportContext) => void
}

export function ComprasTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
  onAccountsPayableContextChange,
}: ComprasTabProps) {
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const isAccountsPayable = activeReport === 'accounts-payable-report'
  const standardReport: PurchaseReportType = isAccountsPayable
    ? 'general'
    : activeReport
  const currentDraft = draftFilters[standardReport]
  const currentApplied = appliedFilters[standardReport]
  const queryFilters = useMemo(
    () => ({ ...currentApplied, page }),
    [currentApplied, page],
  )
  const optionsQuery = useQuery({
    queryKey: ['reports', 'purchases', 'filter-options'],
    queryFn: () => reportsService.getPurchaseFilterOptions(),
    staleTime: 5 * 60 * 1000,
    enabled: !isAccountsPayable,
  })
  const reportQuery = useQuery({
    queryKey: ['reports', 'purchases', standardReport, queryFilters],
    queryFn: () =>
      reportsService.getPurchaseReport<PurchaseRow>(standardReport, queryFilters),
    placeholderData: (previous) =>
      previous?.report === standardReport ? previous : undefined,
    enabled: !isAccountsPayable,
  })

  useEffect(() => {
    if (!isAccountsPayable) {
      onAppliedContextChange({ type: standardReport, filters: currentApplied })
    }
  }, [currentApplied, isAccountsPayable, onAppliedContextChange, standardReport])

  const updateDraft = (changes: Partial<PurchaseReportFilters>) => {
    setDraftFilters((current) => ({
      ...current,
      [standardReport]: { ...current[standardReport], ...changes },
    }))
  }
  const applyFilters = () => {
    setAppliedFilters((current) => ({
      ...current,
      [standardReport]: { ...currentDraft },
    }))
    setPage(1)
  }
  const clearFilters = () => {
    setDraftFilters((current) => ({
      ...current,
      [standardReport]: { ...DEFAULT_FILTERS[standardReport] },
    }))
    setAppliedFilters((current) => ({
      ...current,
      [standardReport]: { ...DEFAULT_FILTERS[standardReport] },
    }))
    setPage(1)
  }

  return (
    <TabsContent value="compras" className="space-y-5">
      <Tabs
        value={activeReport}
        onValueChange={(value) => {
          onActiveReportChange(value as PurchaseReportTab)
          setPage(1)
        }}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="general" className="text-xs sm:text-sm">
            Compras Generales
          </TabsTrigger>
          <TabsTrigger value="products" className="text-xs sm:text-sm">
            Por Producto
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="text-xs sm:text-sm">
            Por Proveedor
          </TabsTrigger>
           <TabsTrigger value="accounts-payable-report" className="text-xs sm:text-sm"> Cuentas por pagar </TabsTrigger>
        </TabsList>

        {isAccountsPayable ? (
          <AccountsPayableReport onContextChange={onAccountsPayableContextChange} />
        ) : (
          <>
            <GlobalFilterBar
              show={['dates']}
              values={{
                startDate: String(currentDraft.date_from ?? ''),
                endDate: String(currentDraft.date_to ?? ''),
              }}
              onChange={(changes) =>
                updateDraft({
                  ...(changes.startDate !== undefined
                    ? { date_from: changes.startDate || undefined }
                    : {}),
                  ...(changes.endDate !== undefined
                    ? { date_to: changes.endDate || undefined }
                    : {}),
                })
              }
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters(standardReport, currentDraft)}
            >
              <SpecificFilters
                type={standardReport}
                filters={currentDraft}
                options={optionsQuery.data}
                onChange={updateDraft}
              />
              <div className="flex items-end gap-2">
                <Button type="button" onClick={applyFilters}>
                  <Filter className="mr-2 h-4 w-4" /> Aplicar filtros
                </Button>
                {optionsQuery.isLoading && (
                  <span className="flex items-center gap-1 pb-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
                  </span>
                )}
              </div>
            </GlobalFilterBar>

            <PurchaseResults
              type={standardReport}
              result={reportQuery.data}
              isLoading={reportQuery.isLoading}
              isFetching={reportQuery.isFetching}
              error={reportQuery.error}
              onPageChange={setPage}
            />
          </>
        )}
      </Tabs>
    </TabsContent>
  )
}

function SpecificFilters({
  type,
  filters,
  options,
  onChange,
}: {
  type: PurchaseReportType
  filters: PurchaseReportFilters
  options?: PurchaseReportFilterOptions
  onChange: (changes: Partial<PurchaseReportFilters>) => void
}) {
  if (type === 'general') {
    return (
      <>
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onChange({ sucursal_id: numberOrUndefined(value) })
          }
        />
        <CatalogField
          label="Proveedor"
          catalog="report-purchase-suppliers"
          placeholder="Todos los proveedores"
          value={filters.supplier_id}
          onChange={(value) =>
            onChange({ supplier_id: numberOrUndefined(value) })
          }
        />
        <OptionsSelect
          label="Forma de pago"
          value={filters.payment_method_id}
          options={options?.payment_methods}
          onChange={(value) =>
            onChange({ payment_method_id: numberOrUndefined(value) })
          }
        />
      </>
    )
  }
  if (type === 'products') {
    return (
      <>
        <CatalogField
          label="Producto"
          catalog="report-purchase-products"
          placeholder="Todos los productos"
          value={filters.product_id}
          onChange={(value) =>
            onChange({ product_id: numberOrUndefined(value) })
          }
        />
        <OptionsSelect
          label="Departamento"
          value={filters.department_id}
          options={options?.departments}
          onChange={(value) =>
            onChange({ department_id: numberOrUndefined(value) })
          }
        />
        <OptionsSelect
          label="Categoría"
          value={filters.category_id}
          options={options?.categories}
          onChange={(value) =>
            onChange({ category_id: numberOrUndefined(value) })
          }
        />
      </>
    )
  }
  return (
    <CatalogField
      label="Proveedor"
      catalog="report-purchase-suppliers"
      placeholder="Todos los proveedores"
      value={filters.supplier_id}
      onChange={(value) => onChange({ supplier_id: numberOrUndefined(value) })}
    />
  )
}

function PurchaseResults({
  type,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: PurchaseReportType
  result?: PurchaseReportResult<PurchaseRow>
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  const headers =
    type === 'general'
      ? [
          'Fecha de compra',
          'Fecha de ingreso',
          'Fecha de registro',
          'Folio',
          'Origen',
          'Proveedor',
          'Sucursal',
          'Usuario',
          'Cuenta del usuario',
          'Forma de pago',
          'Descuento',
          'Descuento %',
          'Importe',
        ]
      : type === 'products'
        ? [
            'Código de barras',
            'Producto',
            'Departamento',
            'Categoría',
            'Cantidad',
            'Unidad',
            'Precio con impuestos',
            'Importe',
          ]
        : [
            'Proveedor',
            'No. compras',
            'Ticket promedio',
            'Descuento',
            'Descuento %',
            'Importe total',
          ]

  if (isLoading)
    return (
      <TableSkeleton
        headers={headers}
        columns={headers.map(() => 'text')}
        rows={10}
      />
    )
  if (error)
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  if (!result || result.rows.length === 0)
    return <ReportState message="No hay compras para los filtros aplicados." />

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total} registros filtrados
          </p>
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-max">
          <TableHeader className="border-border bg-table-header-bg border-b">
            <TableRow className="hover:bg-transparent">
              {headers.map((header, index) => (
                <TableHead
                  key={header}
                  className={`${HEAD_CLASS} ${numericColumn(type, index) ? 'text-right' : ''}`}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <PurchaseRows type={type} result={result} />
        </Table>
      </div>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function PurchaseRows({
  type,
  result,
}: {
  type: PurchaseReportType
  result: PurchaseReportResult<PurchaseRow>
}) {
  if (type === 'general') {
    const rows = result.rows as GeneralPurchaseReportRow[]
    return (
      <>
        <TableBody className="[&_tr]:border-border">
          {rows.map((row, index) => (
            <TableRow
              key={`${row.folio}-${index}`}
              className="hover:bg-table-row-hover"
            >
              <DateCell value={row.purchase_date} />
              <DateCell value={row.entry_date} />
              <DateCell value={row.created_at} withTime />
              <TextCell value={row.folio} strong />
              <TextCell value={row.source} />
              <TextCell value={row.supplier} />
              <TextCell value={row.branch} />
              <TextCell value={row.user} />
              <TextCell value={row.user_account} />
              <TextCell value={row.payment_method} />
              <MoneyCell value={row.discount} />
              <PercentageCell value={row.discount_percentage} />
              <MoneyCell value={row.amount} strong />
            </TableRow>
          ))}
        </TableBody>
        <ReportFooter>
          <TextCell
            value={`TOTAL GENERAL · Registros: ${result.totals.record_count}`}
          />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell value="" />
          <TextCell
            value={unavailableText(
              result.totals.unavailable_amount_count,
              result.totals.unavailable_discount_count,
            )}
          />
          <TextCell value="" />
          <MoneyCell value={result.totals.discount} strong />
          <PercentageCell value={result.totals.discount_percentage} strong />
          <MoneyCell value={result.totals.amount} strong />
        </ReportFooter>
      </>
    )
  }
  if (type === 'products') {
    const rows = result.rows as ProductPurchaseReportRow[]
    return (
      <>
        <TableBody className="[&_tr]:border-border">
          {rows.map((row, index) => (
            <TableRow
              key={`${row.barcode}-${row.product}-${row.average_price}-${index}`}
              className="hover:bg-table-row-hover"
            >
              <TextCell value={row.barcode} />
              <TextCell value={row.product} strong />
              <TextCell value={row.department} />
              <TextCell value={row.category} />
              <NumberCell value={row.quantity} />
              <TextCell value={row.unit} />
              <MoneyCell value={row.average_price} />
              <MoneyCell value={row.amount} strong />
            </TableRow>
          ))}
        </TableBody>
        <ReportFooter>
          <TextCell
            value={`TOTAL GENERAL · Grupos: ${result.totals.record_count}`}
          />
          <TextCell
            value={unavailableText(result.totals.unavailable_amount_count)}
          />
          <TextCell value="" />
          <TextCell value="" />
          <NumberCell value={result.totals.quantity ?? 0} strong />
          <TextCell value="" />
          <MoneyCell value={result.totals.average_price} strong />
          <MoneyCell value={result.totals.amount} strong />
        </ReportFooter>
      </>
    )
  }
  const rows = result.rows as SupplierPurchaseReportRow[]
  return (
    <>
      <TableBody className="[&_tr]:border-border">
        {rows.map((row) => (
          <TableRow key={row.supplier} className="hover:bg-table-row-hover">
            <TextCell value={row.supplier} strong />
            <NumberCell value={row.purchase_count} />
            <MoneyCell value={row.average} />
            <MoneyCell value={row.discount} />
            <PercentageCell value={row.discount_percentage} />
            <MoneyCell value={row.amount} strong />
          </TableRow>
        ))}
      </TableBody>
      <ReportFooter>
        <TextCell
          value={`TOTAL GENERAL · Proveedores: ${result.totals.record_count}`}
        />
        <NumberCell value={result.totals.purchase_count ?? 0} strong />
        <MoneyCell value={result.totals.average} strong />
        <MoneyCell value={result.totals.discount} strong />
        <PercentageCell value={result.totals.discount_percentage} strong />
        <MoneyCell value={result.totals.amount} strong />
      </ReportFooter>
    </>
  )
}

function ReportFooter({ children }: { children: ReactNode }) {
  return (
    <TableFooter className="border-border bg-table-header-bg border-t font-semibold">
      <TableRow className="hover:bg-transparent">{children}</TableRow>
    </TableFooter>
  )
}

function CatalogField({
  label,
  catalog,
  placeholder,
  value,
  onChange,
}: {
  label: string
  catalog: string
  placeholder: string
  value?: number
  onChange: (value: unknown) => void
}) {
  return (
    <Field label={label}>
      <CatalogSelect
        catalog={catalog}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </Field>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  onChange,
}: {
  label: string
  value: unknown
  options?: PurchaseReportFilterOptions['payment_methods']
  onChange: (value: string) => void
}) {
  const selected = value === undefined || value === null ? 'all' : String(value)
  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return (
              <SelectItem key={optionValue} value={optionValue}>
                {option.name ?? option.label ?? optionValue}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function TextCell({
  value,
  strong = false,
}: {
  value: string
  strong?: boolean
}) {
  return (
    <TableCell className={`${CELL_CLASS} ${strong ? 'font-medium' : ''}`}>
      {value}
    </TableCell>
  )
}
function MoneyCell({
  value,
  strong = false,
}: {
  value: string | number | null | undefined
  strong?: boolean
}) {
  return (
    <TableCell
      className={`${CELL_CLASS} text-right tabular-nums ${strong ? 'font-semibold' : ''}`}
    >
      {value === null || value === undefined
        ? 'No disponible'
        : formatMoney(value)}
    </TableCell>
  )
}
function PercentageCell({
  value,
  strong = false,
}: {
  value: string | number | null | undefined
  strong?: boolean
}) {
  return (
    <TableCell
      className={`${CELL_CLASS} text-right tabular-nums ${strong ? 'font-semibold' : ''}`}
    >
      {value === null || value === undefined
        ? 'No disponible'
        : `${Number(value).toFixed(2)}%`}
    </TableCell>
  )
}
function DateCell({
  value,
  withTime = false,
}: {
  value: string | null
  withTime?: boolean
}) {
  return (
    <TableCell className={`${CELL_CLASS} whitespace-nowrap`}>
      {value ? formatReportDate(value, withTime) : '—'}
    </TableCell>
  )
}
function NumberCell({
  value,
  strong = false,
}: {
  value: number
  strong?: boolean
}) {
  return (
    <TableCell
      className={`${CELL_CLASS} text-right tabular-nums ${strong ? 'font-semibold' : ''}`}
    >
      {value.toLocaleString('es-MX', { maximumFractionDigits: 3 })}
    </TableCell>
  )
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}
function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function numberOrUndefined(value: unknown): number | undefined {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}
function hasActiveFilters(
  type: PurchaseReportType,
  filters: PurchaseReportFilters,
) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])
  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}
function numericColumn(type: PurchaseReportType, index: number) {
  return type === 'general'
    ? index >= 10
    : type === 'products'
      ? [4, 6, 7].includes(index)
      : index >= 1
}
function unavailableText(amountCount: number, discountCount = 0) {
  const messages = []
  if (amountCount > 0) messages.push(`Sin importe: ${amountCount}`)
  if (discountCount > 0) messages.push(`Sin descuento: ${discountCount}`)
  return messages.join(' · ')
}
function formatMoney(value: string | number) {
  return Number(value).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}
function formatReportDate(value: string, withTime: boolean) {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/,
  )
  if (!match) return value

  const [, year, month, day, hours = '00', minutes = '00'] = match
  return withTime
    ? `${day}/${month}/${year} ${hours}:${minutes}`
    : `${day}/${month}/${year}`
}
